import { FC } from 'react';
import { t } from 'i18next';
import { theme } from '@huspy/forge';
import {
  Text, Flex, Box
} from '@huspy/briks-web';
import { Divider } from '@mantine/core';
import useUpdateNote from '@modules/clients/entities/notes/mutation/useUpdateNote';
import modals from '@shared/modals';
import useDeleteNote from '@modules/clients/entities/notes/mutation/useDeleteNote';
import FilledPinIcon from '@modules/clients/presentation/v1/pages/Details/icons/FilledPinIncon';
import DeleteIcon from '@modules/clients/presentation/v1/pages/Details/icons/DeleteIcon';
import PinIcon from '@modules/clients/presentation/v1/pages/Details/icons/PinIcon';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { clientCaseCardStyles } from './styles/index.css';

type NoteCardType = {
  external_id: string,
  link_external_id: string,
  text: string,
  name?: string,
  is_pinned: boolean,
  created_at: string,
  onClickNote: () => void;
};

const NoteCard: FC<NoteCardType> = ({
  external_id,
  link_external_id,
  text,
  name,
  created_at,
  is_pinned,
  onClickNote,
}) => {
  const { mutateAsync } = useUpdateNote(link_external_id);
  const { mutateAsync: deleteMutation } = useDeleteNote(link_external_id);
  const date = new Date(created_at);

  const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${
    (date.getMonth() + 1).toString().padStart(2, '0')}/${
    date.getFullYear().toString()}`;

  const togglePin = async () => {
    await mutateAsync({
      external_id,
      link_external_id,
      link_type: 'client',
      is_pinned: !is_pinned,
    });
    trackAmplitudeEvent(USER_EVENTS.CLIENT.NOTE_PIN_CLICKED, { client_id: link_external_id });
  };

  const onClickDelete = () => {
    modals.confirm({
      title: t('notes.deleteNote.modal.title'),
      children: (
        <Text size='sm'>
          {t('notes.deleteNote.modal.children')}
        </Text>
      ),
      confirmCtaText: t('notes.deleteNote.modal.deleteText'),
      cancelCtaText: t('notes.deleteNote.modal.cancelText'),
      onConfirm: async () => {
        await deleteMutation(external_id);
        trackAmplitudeEvent(USER_EVENTS.CLIENT.DELETE_NOTE_CLICKED, { client_id: link_external_id });
      },
    });
  };

  return (
    <Box
      padding={ 5 }
      className={ clientCaseCardStyles.box }
      h='100%'
    >
      <Flex direction='column' gap={ 4 } h='100%'>
        <Text
          onClick={ onClickNote }
          color='neutral.8'
          size='md'
          h='100%'
          maxH='100px'
          style={ {
            cursor: 'pointer',
            wordBreak: 'break-word',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',

          } }
        >
          {text}
        </Text>
        <Flex direction='column' gap={ 4 }>
          <Divider
            color={ theme.colors.neutral[2] }
            h='1px'
          />
          <Flex justify='space-between' align='center' gap={ 4 }>
            <Flex color='text-secondary' gap={ 2 } align='center'>
              { name
              && (
                <>
                  <Text size='md'>
                    {`${t('notes.note.createdBy')} ${name}`}
                  </Text>
                  <Text size='md' weight='semiBold'>
                    •
                  </Text>
                </>
              )}

              <Text size='md'>
                {formattedDate}
              </Text>
            </Flex>
            <Flex gap={ 4 }>
              {is_pinned ? <FilledPinIcon onClick={ togglePin } data-test='note-filled-pin' />
                : <PinIcon onClick={ togglePin } data-test='note-pin' />}
              <DeleteIcon
                data-test='delete-note-btn'
                onClick={ onClickDelete }
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default NoteCard;
